import { useCallback } from "react";
import { useStore } from "../contexts/StoreContext";
import Api from "../utils/Api";
import { Movie } from '../domains/resource/movie';
import { MovieDisplay } from "../utils/Video";
/**
 * 再生位置保存処理
 * @param customerId
 * @param movieId
 * @param isRecording
 * @returns useCallback関数(再生位置保存処理が実行される。動画が永続済みかそうでないかで処理内様が変わる。)
 */
export default function usePlaybackPositionSetter(
    customerId: string,
    movieId: number,
    isRecording: boolean,
): (currentPlayingTime: number) => Promise<Movie.Resource | undefined> {
    const { recordingStore } = useStore();
    // 永続化済みの動画とそうでない動画を同様に扱うためResource型で動画情報を返す
    // RecordingStoreから非永続化の動画を検索する際undefinedを返す可能性があるため、その際はそのままundefinedを返す
    const setter = useCallback(async (currentPlayingTime: number): Promise<Movie.Resource | undefined> => {
        // 永続化していない動画の場合、Store内の動画情報を更新する
        if (isRecording) {
            const recordedVideo = recordingStore.getSingleRegisteredVideo(movieId);
            if (recordedVideo) {
                // 再度Storeを検索し、対象の動画が存在していれば動画保存位置を設定
                recordingStore.setPlaybackPosition(
                    customerId,
                    movieId,
                    currentPlayingTime
                );
            }
            const latestRecordedVideo = recordingStore.getSingleRegisteredVideo(movieId);
            if (latestRecordedVideo && latestRecordedVideo.video) {
                const resourceFromRecordedVideo =
                await Movie.fromRawData(customerId, latestRecordedVideo.video, latestRecordedVideo).catch(() => undefined);

                return resourceFromRecordedVideo;
            }
            return undefined;
        }
        // 永続化されている動画の場合、動画再生位置保存のAPIをkickする
        const responseMovieEntity = await Api.setPlaybackPosition(`${movieId}`, currentPlayingTime);
        const latestMovieDisplay = new MovieDisplay(responseMovieEntity);
        const resourceFromMovieDisplay = Movie.fromEntity(latestMovieDisplay);
        return resourceFromMovieDisplay;
    }, [isRecording, customerId, movieId, recordingStore]);
    return setter;
}
